import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface SwipeHintProps {
  storageKey: string;
  label?: string;
  className?: string;
  /** Auto-dismiss timeout in ms. Default 4000. */
  timeoutMs?: number;
}

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * Mobile-only first-load swipe affordance.
 * Renders nothing on desktop or after the user has seen it once.
 * Self-dismisses on first pointer interaction or after `timeoutMs`.
 */
const SwipeHint = ({
  storageKey,
  label = "Swipe to explore",
  className = "",
  timeoutMs = 4000,
}: SwipeHintProps) => {
  const isMobile = useIsMobile();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isMobile) return;
    if (typeof window === "undefined") return;
    try {
      if (window.localStorage.getItem(storageKey) === "1") return;
    } catch {
      // localStorage unavailable — still show the hint, just don't persist.
    }
    // Small delay so it fades in after the hero settles.
    const showId = window.setTimeout(() => setVisible(true), 400);
    return () => window.clearTimeout(showId);
  }, [isMobile, storageKey]);

  useEffect(() => {
    if (!visible) return;
    const dismiss = () => {
      setVisible(false);
      try {
        window.localStorage.setItem(storageKey, "1");
      } catch {
        /* noop */
      }
    };
    const timeoutId = window.setTimeout(dismiss, timeoutMs);
    const onInteract = () => dismiss();
    window.addEventListener("pointerdown", onInteract, { once: true });
    window.addEventListener("touchstart", onInteract, { once: true });
    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener("pointerdown", onInteract);
      window.removeEventListener("touchstart", onInteract);
    };
  }, [visible, storageKey, timeoutMs]);

  if (!isMobile) return null;

  const reduce = prefersReducedMotion();

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className={`pointer-events-none flex items-center gap-2 rounded-full bg-charcoal/55 backdrop-blur-sm px-4 py-2 text-stone/85 font-sans font-light text-[10px] tracking-[0.2em] uppercase ${className}`}
          aria-hidden
        >
          <motion.span
            animate={reduce ? undefined : { x: [0, -4, 0] }}
            transition={reduce ? undefined : { duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            className="inline-flex"
          >
            <ChevronLeft className="w-3 h-3" strokeWidth={1.5} />
          </motion.span>
          <span>{label}</span>
          <motion.span
            animate={reduce ? undefined : { x: [0, 4, 0] }}
            transition={reduce ? undefined : { duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            className="inline-flex"
          >
            <ChevronRight className="w-3 h-3" strokeWidth={1.5} />
          </motion.span>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SwipeHint;
